import React, { useEffect, useReducer, useState } from 'react';
import { useEnvironment, useHelmet } from '../../hooks';
import { useSnackbar } from '../../hooks/snackbar';
import { useStyles } from './styles';
import ListaVazia from '../../components/util/listaVazia/.mixins/ListaVazia';
import { Filters, IApplyFilters } from './components/filters';
import { Totals } from './components/totals';
import { Table } from '../../components/migrated/table';
import { CircularProgress, Tooltip, Typography } from '@material-ui/core';
import { usePaymentsGql } from './graphql/payments';
import { initialPaymentState, reducerPayments } from './reducers/reducer';
import EyeIcon from '@material-ui/icons/RemoveRedEye';
import { ITableData } from './interfaces';
import { Modal } from 'components/migrated/modal';
import moment from 'moment';
import { IPaymentsDelay } from './components/payment-delay/interfaces';
import { PaymentDelay } from './components/payment-delay';

export const Payments = () => {
  const classes = useStyles();
  const { toggleSnackbar } = useSnackbar();
  const { TITLE, DEBUG } = useEnvironment();
  useHelmet({ title: `${TITLE} - Pagamentos` });
  const { runPaymentsGql, dataPayments, errorPayments, loadingPayments } = usePaymentsGql();
  const [state, dispatch] = useReducer(reducerPayments, initialPaymentState);
  const [flagType, setFlagType] = useState('');
  const [supplier, setSupplier] = useState('');
  const [tableData, setTableData] = useState<ITableData[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [args, setArgs] = useState<IPaymentsDelay['args']>({
    invoiceNumber: 0,
    personId: 0,
    invoiceSerieDescription: '',
    invoiceSubSerieNumber: 0,
    issueDate: ''
  });

  const handleDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR').toString();
  };

  const handlerModal = (meta: any) => {
    handlePaymentDelayArgs(meta.rowIndex);
    setModalOpen(!modalOpen);
  };

  const handlePaymentDelayArgs = (index: number) => {
    const payment = dataPayments[index];
    const args = {
      invoiceNumber: payment.invoiceNumber,
      personId: payment.paymentDetail[0].personId,
      invoiceSerieDescription: payment.paymentDetail[0].invoiceSerieDescription,
      invoiceSubSerieNumber: payment.paymentDetail[0].invoiceSubSerieNum,
      issueDate: payment.paymentDetail[0].issueDate
    };
    setArgs(args);
  };

  const handleDateSplit = (date: string) => {
    return date ? date.split(' ')[0] : '';
  };

  const handleCurrency = new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency'
  });

  const handleLoadData = async (args: IApplyFilters) => {
    const startDate = handleDate(args.startDate);
    const endDate = handleDate(args.endDate);
    const personId = parseInt(args.supplier);

    runPaymentsGql({
      variables: {
        endDate,
        initialRow: 0,
        nextRows: 9999,
        personId,
        startDate
      }
    });
    setFlagType(args.flagType);
    setSupplier(args.supplier);
  };

  const handleDataTable = () => {
    const data: ITableData[] = [];

    const dataPaymentsPromise = dataPayments.map((payment) => {
      const payDay = moment(payment.paymentDate, 'DD/MM/YYYY');
      const daysUntilPayment = payDay.diff(moment(), 'days');
      const isAfter = moment().isAfter(payDay);
      const isSameOrBefore = moment().isSameOrBefore(payDay);

      payment.paymentDetail.map((detail) => {
        const haveDelay = detail.delayPaymentTerm > 0;
        const amountPaid = handleCurrency.format(isAfter ? payment.paidValue : 0); // Valor Pago
        const amountReceivable = handleCurrency.format(isSameOrBefore ? payment.paidValue : 0); // Valor a receber
        const anticipationOptionsReceipt = haveDelay ? `${detail.advancePaymentTerm} dias` : '-'; // PRAZO_ANTECIPACAO_PAGAMENTO
        const deliveryDate = handleDateSplit(detail.receiptDate); // DT_RECEBIMENTO
        const discountForAnticipation = handleCurrency.format(payment.totalAmountAdvanceDiscount); // DESC_ANTECIPADO
        const discountForDownload = handleCurrency.format(payment.downloadDiscountValue); // DESC_DESCARGA
        const discountForResale = handleCurrency.format(payment.resaleDiscountValue); //DESC_REVENDA
        const discountForTotalGC = handleCurrency.format(payment.totalGCDiscountValue); //DESC_TOT_GC
        const discountNoShow = handleCurrency.format(payment.totalNoShowPenalty); // DESC_NOSHOW
        const interestAccrualForReturn = handleCurrency.format(payment.totalAddReturnInvoice); // ACRES_DEVOLUCAO
        const invoiceNumber = payment.invoiceNumber; // NOTA_FISCAL
        const invoiceValue = handleCurrency.format(payment.totalAmount); // VALOR_NOTA
        const lateChangeDiscount = handleCurrency.format(haveDelay ? detail.schedulePenaltyAmount : 0); // VM_PENALIZA_TARDIA
        const paymentDate = handleDateSplit(payment.paymentDate); // DT_PAGAMENTO
        const paymentTerm = haveDelay ? `${detail.personPaymentTerm} dias` : '-'; // PRAZO_PAGAMENTO
        const penaltyOnPaymentTerm = detail.delayPaymentTerm; // DADOS_COMPLEMENTARES
        const returnInvoiceAmount = handleCurrency.format(Math.abs(payment.totalAmountReturnInvoice)); // VL_NF_DEVOL
        const returnInvoiceNumber = payment.returnInvoiceNumber || '-'; // NOTA_DEVOLUCAO
        const totalDaysUntilPayment = daysUntilPayment > 0 ? `${daysUntilPayment.toString()} dias` : '-'; // QT_DIAS_PAGAMENTO

        const newPayment = {
          amountPaid,
          amountReceivable,
          anticipationOptionsReceipt,
          deliveryDate,
          discountForAnticipation,
          discountForDownload,
          discountForResale,
          discountForTotalGC,
          discountNoShow,
          interestAccrualForReturn,
          invoiceNumber,
          invoiceValue,
          lateChangeDiscount,
          paymentDate,
          paymentTerm,
          penaltyOnPaymentTerm,
          returnInvoiceAmount,
          returnInvoiceNumber,
          totalDaysUntilPayment
        };

        if (flagType.includes('Pagos') && isAfter) {
          data.push(newPayment);
        }

        if (flagType.includes('Pendentes') && isSameOrBefore) {
          data.push(newPayment);
        }

        if (flagType.includes('Todos')) {
          data.push(newPayment);
        }
      });
    });

    Promise.all(dataPaymentsPromise).then(() => {
      setTableData([...data]);
    });
  };

  useEffect(() => {
    if (errorPayments) {
      if (DEBUG === 'TRUE') {
        console.log(`Erro ao carregar dados! Error:(message: ${errorPayments} )`);
      } else {
        toggleSnackbar({
          message: 'Ocorreu um problema ao carregar os dados. Por favor, informe ao suporte.',
          type: 'error'
        });
      }
    }
  }, [errorPayments]);

  useEffect(() => {
    handleDataTable();
    dispatch({ type: 'TOTALS', payload: dataPayments, flagType });
  }, [loadingPayments]);

  useEffect(() => {
    handleDataTable();
    dispatch({ type: 'TOTALS', payload: dataPayments, flagType });
  }, [flagType]);

  useEffect(() => {
    handleDataTable();
    dispatch({ type: 'TOTALS', payload: dataPayments, flagType });
  }, [supplier]);

  const columns = [
    {
      label: 'Data Entrega',
      name: 'deliveryDate'
    },
    {
      label: 'Prazo Pagamento',
      name: 'paymentTerm'
    },
    {
      label: 'Antecipação Opções Recebimento',
      name: 'anticipationOptionsReceipt'
    },
    {
      label: 'Penalização em prazo de pagamento',
      name: 'penaltyOnPaymentTerm',
      options: {
        display: true,
        filter: false,
        download: true,
        viewColumns: true,
        customBodyRender: (value: number, meta: any) => (
          <>
            {value > 0 ? (
              <Tooltip title="Ver Detalhes" arrow>
                <div className={classes.penaltyOnPaymentTerm} onClick={() => handlerModal(meta)}>
                  {value} <EyeIcon style={{ color: '#129B78', marginLeft: '6px' }} fontSize="small" />
                </div>
              </Tooltip>
            ) : (
              <div className={classes.penaltyOnPaymentTerm}>
                <Typography>-</Typography>
              </div>
            )}
          </>
        )
      }
    },
    {
      label: 'Dias totais até o pagamento',
      name: 'totalDaysUntilPayment'
    },
    {
      label: 'Data Pagamento',
      name: 'paymentDate'
    },
    {
      label: 'Número NF',
      name: 'invoiceNumber'
    },
    {
      label: 'Valor da NF',
      name: 'invoiceValue'
    },
    {
      label: 'Número NF Devolução',
      name: 'returnInvoiceNumber'
    },
    {
      label: 'Valor da NF Devolução',
      name: 'returnInvoiceAmount'
    },
    {
      label: 'Desc. Acresc. Devolução',
      name: 'interestAccrualForReturn'
    },
    {
      label: 'Desc. Antecipação',
      name: 'discountForAnticipation'
    },
    {
      label: 'Desc. NoShow',
      name: 'discountNoShow'
    },
    {
      label: 'Desc. Descarga',
      name: 'discountForDownload'
    },
    {
      label: 'Desc. Revenda',
      name: 'discountForResale'
    },
    {
      label: 'Outros Desc. GC',
      name: 'discountForTotalGC'
    },
    {
      label: 'Desc. Alteração Tardia',
      name: 'lateChangeDiscount'
    },
    {
      label: 'Valor Pago',
      name: 'amountPaid',
      options: {
        display: flagType.includes('Pendentes') ? false : true
      }
    },
    {
      label: 'Valor a receber',
      name: 'amountReceivable',
      options: {
        display: flagType.includes('Pagos') ? false : true
      }
    }
  ];

  return (
    <>
      <Modal open={modalOpen} onClose={setModalOpen} title="DETALHAMENTO PENALIZAÇÃO EM PRAZO DE PAGAMENTO">
        <PaymentDelay args={args} />
      </Modal>

      <div className="container">
        <Filters onApplyFilters={(args) => handleLoadData(args)} />

        {!loadingPayments && <Totals totals={state.totals} />}

        {!loadingPayments && tableData.length > 0 && <Table columns={columns} data={tableData} title="Pagamentos" />}

        {loadingPayments && (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        )}

        {!loadingPayments && tableData.length === 0 && (
          <ListaVazia
            tipoMensagem="Alert"
            text="Não encontramos pagamentos"
            textDescription="Busque por outro intervalo de Datas ou Tipo"
          />
        )}
      </div>
    </>
  );
};
